import { DataClass, load } from 'scrivito';
import { z } from 'zod';

import { MAX_ITEMS } from './config';
import { formatDataItemDetails } from './format_data_item';
import { resolveTool } from './tool_integration';

export function defineCRUDToolsV1(dataClasses: DataClass[]) {
  const classNames = dataClasses
    .map((dataClass) => dataClass.name())
    .filter((n): n is string => n !== null);

  return [
    resolveTool({
      name: 'list',

      description: `Get a list of data for the given data class. Returns at most ${MAX_ITEMS} items per call. If the user asks for "all" items, inform them that you can only show a limited number at a time.`,

      parameters: z.object({
        dataClass: z.enum(classNames).describe('The kind of data to fetch.'),
        limit: z
          .number()
          .describe(`how many items to fetch (default: 10, max: ${MAX_ITEMS})`)
          .optional(),
        filter: z
          .object({
            attribute: z
              .string()
              .describe('the attribute which must have the given value'),
            value: z
              .union([z.string(), z.boolean()])
              .describe('the value that the attribute must have'),
          })
          .optional(),
        order: z
          .object({
            attribute: z.string().describe('the attribute to sort on'),
            direction: z.enum(['asc', 'desc']),
          })
          .optional(),
        search: z
          .string()
          .describe('search for keywords in the full text.')
          .optional(),
      }),

      run: async (params) => {
        const DataKlass = dataClassByName(params.dataClass);

        const order = params.order;
        const filter = params.filter;

        const dataScope = DataKlass.all().transform({
          filters: filter && { [filter.attribute]: filter.value },
          order: order && [[order.attribute, order.direction]],
          limit: params.limit ? Math.min(params.limit, MAX_ITEMS) : 10,
          search: params.search,
        });

        const items = await load(() => dataScope.take());
        const data = await Promise.all(items.map(formatDataItemDetails));
        // eslint-disable-next-line no-console
        console.log(data);
        return data;
      },
    }),

    resolveTool({
      name: 'count',

      description:
        'Count items of the given data class, optionally filtered or searched.',

      parameters: z.object({
        dataClass: z.enum(classNames).describe('The kind of data to count.'),
        filter: z
          .object({
            attribute: z
              .string()
              .describe('the attribute which must have the given value'),
            value: z
              .union([z.string(), z.boolean()])
              .describe('the value that the attribute must have'),
          })
          .optional(),
        search: z
          .string()
          .describe('search for keywords in the full text.')
          .optional(),
      }),

      run: async ({ dataClass, filter, search }) => {
        const DataKlass = dataClassByName(dataClass);

        const dataScope = DataKlass.all().transform({
          filters: filter && { [filter.attribute]: filter.value },
          search,
        });

        const count = await load(() => dataScope.count());

        if (count === null) {
          return `The data backend for '${dataClass}' does not support counting.`;
        }

        return { count };
      },
    }),

    resolveTool({
      name: 'create',

      description: 'Create a new item of the given data class.',

      parameters: z.object({
        dataClass: z
          .enum(classNames)
          .describe('The data class to create an item for.'),
        data: z
          .array(
            z.object({
              attribute: z.string().describe('the name of the attribute'),
              value: z.string().describe('the value to set for the attribute'),
            }),
          )
          .describe(
            'an list of attribute values that should be set for the new item.',
          ),
      }),

      run: async (params) => {
        const DataKlass = dataClassByName(params.dataClass);

        const data = Object.fromEntries(
          params.data.map(({ attribute, value }) => [attribute, value]),
        );

        const item = await DataKlass.create(data);

        return formatDataItemDetails(item);
      },
    }),
  ];

  function dataClassByName(name: string) {
    const DataKlass = dataClasses.find((klass) => klass.name() === name);

    if (!DataKlass) {
      throw new Error(`unknown data class ${name}`);
    }

    return DataKlass;
  }
}
